import React, { useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getDepositAmountPercentage, isDefaultDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getCheapestSelectedPackageOption, getCheapestAvailablePackageOption } from 'selectors/offerPage/offerPageSelectors'
import GeoContext from 'contexts/geoContext'
import ReserveForZeroPolicyModal from '../ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import ReserveForZeroAndDepositPolicyModal from '../ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import ModalContext from 'contexts/ModalContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import moment from 'moment'

const Container = styled.div`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  display: flex;
  padding: ${rem(16)};
`

const RightSide = styled.div`
  margin-left: ${rem(8)};

  ${mediaQueryUp.tablet} {
    display: flex;
    align-items: baseline;
  }
`

const StyledLink = styled(TextLink)`
  ${mediaQueryUp.desktop} {
    margin-left: ${rem(8)};
  }
`

interface Props {
  packageView: App.OfferPackageOptionView
  offer: App.Offer
  checkIn: string
  defaultDepositAmountPercentage: number
}

function ReserveForZeroPaymentBanner({ offer, checkIn, packageView, defaultDepositAmountPercentage }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { currentCurrency } = useContext(GeoContext)
  const { hotelTotal } = packageView

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, hotelTotal)

  const isOfferUsingDefaultDepositPercentage = isDefaultDepositAmountPercentage(offer)

  const depositOptionMessage = isOfferUsingDefaultDepositPercentage
    ? intl.formatMessage({
        id: '+Jjhfkj2ss',
        defaultMessage: 'Reserve your booking for free or with a deposit',
        description: 'Body text explaining reserve for free or deposit options without percentage',
      })
    : intl.formatMessage({
        id: 'bovz7ykGEs',
        defaultMessage: 'Reserve your booking for free or with a {depositPercentage}% deposit',
        description: 'Body text explaining reserve for free or deposit options with percentage',
      }, { depositPercentage: depositAmountPercentage })

  const onFindOutMore = useCallback(async() => {
    if (depositsAvailable) {
      showModal(
        <ReserveForZeroAndDepositPolicyModal
          depositAmountPercentage={depositAmountPercentage}
          offerType={offer.type}
          depositThresholds={offer.depositThresholds}
          depositAmount={offer.depositAmount}
        />,
      )
    } else {
      showModal(
        <ReserveForZeroPolicyModal />,
      )
    }
  }, [depositsAvailable, showModal, depositAmountPercentage, offer])

  return (
    <Container>
      <LineInfoCircleIcon colour="primary" />
      <RightSide>
        {depositsAvailable && (
          <BodyText variant="medium">
            <BodyText as="span" variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'OlMugI/lya',
                defaultMessage: 'Flexible payment options:\u00a0',
                description: 'Bold label for flexible payment options banner',
              })}
            </BodyText>
            {depositOptionMessage}
          </BodyText>
        )}
        {!depositsAvailable && (
          <BodyText variant="medium">
            <BodyText as="span" variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'oMNpwRSJzm',
                defaultMessage: 'Reserve for free:\u00a0',
                description: 'Bold label for reserve for free banner',
              })}
            </BodyText>
            <FormattedMessage
              id="2k7APrA2Rx"
              defaultMessage="We'll hold your reservation for 48 hours so you have time to think it over."
              description="Body text explaining 48 hour reservation hold"
            />
          </BodyText>
        )}
        <StyledLink onClick={onFindOutMore} size="medium">
          <FormattedMessage
            id="Ip8Ra9FR50"
            defaultMessage="Learn more"
            description="Link text to learn more about payment options"
          />
        </StyledLink>
      </RightSide>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  packageView: getCheapestSelectedPackageOption(appState, state) ?? getCheapestAvailablePackageOption(state, appState),
  offer: state.offer,
  checkIn: state.checkInDate,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}), ReserveForZeroPaymentBanner)
